import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Owner-side event data layer (React Query).
 *
 * Replaces the old localStorage `EventsContext`. Cache keys are user-scoped
 * (`['events', userId]`) so a logout / user switch cleans up via
 * `queryClient.clear()`, mirroring `useCampaigns`.
 *
 * Mutations hydrate the list cache directly from their responses instead of
 * firing a blind refetch — the API returns the full event shape, so there's
 * no reason to round-trip `GET /api/events` just to learn what we already know.
 */

export const eventsQueryKey = (userId) => ['events', userId || 'anonymous'];

export const eventReviewersQueryKey = (userId) => [
  'knownEventReviewers',
  userId || 'anonymous',
];

// ── Cache helpers ──────────────────────────────────────────────────────────

/** Insert or merge an event into the user's list cache (newest-first). */
function upsertEventInCache(queryClient, userId, event) {
  if (!event?.id) return;
  queryClient.setQueryData(eventsQueryKey(userId), (old) => {
    const list = Array.isArray(old) ? old : [];
    const idx = list.findIndex((e) => e.id === event.id);
    if (idx === -1) return [event, ...list];
    const next = list.slice();
    next[idx] = { ...next[idx], ...event };
    return next;
  });
}

/** Shallow-patch a single event already present in the list cache. */
function patchEventInCache(queryClient, userId, eventId, patch) {
  queryClient.setQueryData(eventsQueryKey(userId), (old) => {
    if (!Array.isArray(old)) return old;
    return old.map((e) => (e.id === eventId ? { ...e, ...patch } : e));
  });
}

// ── Queries ─────────────────────────────────────────────────────────────────

export function useEvents() {
  const { user } = useAuth();
  return useQuery({
    queryKey: eventsQueryKey(user?.id),
    queryFn: async () => {
      const { data } = await api.get('/api/events');
      return Array.isArray(data) ? data : [];
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000,
  });
}

export function useEvent(eventId) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['event', user?.id, eventId],
    queryFn: async () => {
      const { data } = await api.get(`/api/events/${eventId}`);
      return data;
    },
    enabled: Boolean(user?.id && eventId),
    staleTime: 30 * 1000,
    retry: (count, err) => {
      const s = err?.response?.status;
      if (s === 404 || s === 403) return false;
      return count < 1;
    },
  });
}

// ── Content mutations ────────────────────────────────────────────────────────

export function useCreateEvent() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post('/api/events', payload);
      return data;
    },
    // The POST returns the created event — drop it straight into the cache so
    // it shows up immediately (in Drafts) without a list refetch.
    onSuccess: (data) => upsertEventInCache(queryClient, user?.id, data),
  });
}

export function useUpdateEvent() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }) => {
      const { data } = await api.patch(`/api/events/${id}`, patch);
      return data;
    },
    onSuccess: (data, vars) => {
      upsertEventInCache(queryClient, user?.id, data);
      queryClient.invalidateQueries({ queryKey: ['event', user?.id, vars.id] });
    },
  });
}

/**
 * Reopen a pending event for editing. The server reverts it to a draft
 * (compliance_status -> null), revokes the reviewer's link, and emails them.
 * Reconciling from the returned event flips `isLockedForReview` off in the
 * wizard, so the form unlocks without a manual refetch.
 */
export function useReopenEvent() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await api.post(
        `/api/event-compliance/events/${id}/reopen`,
      );
      return data?.event;
    },
    onSuccess: (event, id) => {
      upsertEventInCache(queryClient, user?.id, event);
      queryClient.invalidateQueries({ queryKey: ['event', user?.id, id] });
    },
  });
}

export function useDeleteEvent() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/api/events/${id}`);
      return id;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: eventsQueryKey(user?.id) }),
  });
}

// ── Submit-for-review (optimistic) ───────────────────────────────────────────

/**
 * Optimistic "submit for review" actions (pick a known reviewer / invite a new
 * one). Picking or inviting flips the event to pending on the server; we
 * acknowledge instantly by flipping the cached event to pending, then let the
 * network reconcile in the background.
 *
 * Deliberately NOT built on `useMutation`: the caller closes the modal and
 * navigates away the moment this fires, which unmounts the component. React
 * Query v5 drops observer-bound mutation callbacks on unmount, so reconcile /
 * rollback would be lost. Driving it through the app-scoped `queryClient` and a
 * plain promise keeps those side effects running after unmount.
 */
export function useSubmitEventForReview() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const userId = user?.id;

  const run = useCallback(
    async (eventId, request, errorTitle) => {
      if (!eventId) return;
      const key = eventsQueryKey(userId);
      const previous = queryClient.getQueryData(key);

      // Optimistic flip — runs synchronously before the first await, so a
      // caller that navigates immediately lands on a list already showing the
      // card under Pending Review.
      patchEventInCache(queryClient, userId, eventId, {
        status: 'submitted',
        complianceStatus: 'pending',
      });

      try {
        const { data } = await request();
        // Reconcile from server truth (full serialized event).
        if (data?.event) upsertEventInCache(queryClient, userId, data.event);
        // Background safety net: pull the full list (covers a cold cache that
        // only held this one optimistic entry). Runs after the write commits,
        // so it can't revert the card.
        queryClient.invalidateQueries({ queryKey: key });
      } catch (err) {
        queryClient.setQueryData(key, previous);
        queryClient.invalidateQueries({ queryKey: key });
        toast({
          title: errorTitle,
          description:
            err?.response?.data?.detail || err?.message || 'Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    },
    [queryClient, toast, userId],
  );

  const addReviewer = useCallback(
    (eventId, reviewerUserId) =>
      run(
        eventId,
        () =>
          api.post(
            `/api/event-compliance/events/${eventId}/reviewers/${reviewerUserId}`,
          ),
        "Couldn't send for review",
      ),
    [run],
  );

  const invite = useCallback(
    (eventId, { email, contextNote }) =>
      run(
        eventId,
        () =>
          api.post(`/api/event-compliance/events/${eventId}/invite`, {
            email,
            context_note: contextNote || null,
          }),
        "Couldn't send invitation",
      ),
    [run],
  );

  return { addReviewer, invite };
}
